import { useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import { tenantLoginUrl } from '@/lib/tenantUrl';
import { GlassDialog } from '@/components/GlassDialog';

export interface AdminCredsView {
  username: string;
  password: string;
  created: boolean;
  nameLa: string;
  nameEn: string;
  pathName: string;
}

const companyLabel = (c: AdminCredsView) => `${c.nameLa}(${c.nameEn})`;

/** The block handed to the customer as-is, so they get the site, the user and the password together. */
const handoffText = (c: AdminCredsView) =>
  `${companyLabel(c)}\n${tenantLoginUrl(c.pathName)}\n\nuser ${c.username}\npassword ${c.password}`;

export function AdminCredentialsDialog({ creds, open, onClose }: { creds: AdminCredsView | null; open: boolean; onClose: () => void }) {
  const [copiedAll, setCopiedAll] = useState(false);
  const copy = (v: string) => { void navigator.clipboard?.writeText(v); };
  const copyAll = () => {
    if (!creds) return;
    copy(handoffText(creds));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };
  // The password is shown only once — don't let a stray backdrop click or Escape
  // discard it. Only the explicit "ປິດ" button closes this dialog.
  const guardedClose = (_e: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose();
  };
  return (
    <GlassDialog open={open} onClose={guardedClose} fullWidth maxWidth="sm">
      <DialogTitle>{creds?.created ? 'ສ້າງ Admin ສຳເລັດ' : 'ຣີເຊັດລະຫັດ Admin ສຳເລັດ'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Alert severity="warning">ບັນທຶກລະຫັດຜ່ານນີ້ໄວ້ — ຈະສະແດງພຽງຄັ້ງດຽວ</Alert>
          <TextField label="ບໍລິສັດ" value={creds ? companyLabel(creds) : ''} multiline slotProps={{ input: { readOnly: true,
            endAdornment: <InputAdornment position="end"><IconButton onClick={() => copy(creds ? companyLabel(creds) : '')} aria-label="copy company"><ContentCopyIcon fontSize="small" /></IconButton></InputAdornment> } }} fullWidth />
          <TextField label="ລິງເຂົ້າລະບົບ" value={creds ? tenantLoginUrl(creds.pathName) : ''} multiline slotProps={{ input: { readOnly: true,
            endAdornment: <InputAdornment position="end"><IconButton onClick={() => copy(creds ? tenantLoginUrl(creds.pathName) : '')} aria-label="copy login url"><ContentCopyIcon fontSize="small" /></IconButton></InputAdornment> } }} fullWidth />
          <TextField label="Username" value={creds?.username ?? ''} slotProps={{ input: { readOnly: true,
            endAdornment: <InputAdornment position="end"><IconButton onClick={() => copy(creds?.username ?? '')} aria-label="copy username"><ContentCopyIcon fontSize="small" /></IconButton></InputAdornment> } }} fullWidth />
          <TextField label="Password" value={creds?.password ?? ''} slotProps={{ input: { readOnly: true,
            endAdornment: <InputAdornment position="end"><IconButton onClick={() => copy(creds?.password ?? '')} aria-label="copy password"><ContentCopyIcon fontSize="small" /></IconButton></InputAdornment> } }} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={copyAll} variant="outlined" startIcon={<ContentCopyIcon />}>
          {copiedAll ? 'ຄັດລອກແລ້ວ ✓' : 'ຄັດລອກທັງໝົດ'}
        </Button>
        <Button onClick={onClose} variant="contained">ປິດ</Button>
      </DialogActions>
    </GlassDialog>
  );
}
